import { useState, useEffect, useCallback } from 'react'
import { Box, Flex, Heading, Button, Tabs } from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import {
  BudgetTable,
  BudgetCreateModal,
  FundCallTable,
  FundCallCreateModal,
  FiscalYearSelector,
  BudgetDetailDrawer,
  FundCallDetailDrawer,
  ALURFundAlert,
} from '../../components'
import { useAccountingContext } from '../../context/AccountingContext'
import { useBudgets, useFundCalls } from '../../hooks'

export function BudgetsTab() {
  const { residenceId, activeFiscalYear, setSelectedFiscalYear } = useAccountingContext()
  const fiscalYearId = activeFiscalYear?.id

  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false)
  const [isFundCallModalOpen, setIsFundCallModalOpen] = useState(false)

  // Drawer states
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null)
  const [isBudgetDrawerOpen, setIsBudgetDrawerOpen] = useState(false)
  const [selectedFundCallId, setSelectedFundCallId] = useState<string | null>(null)
  const [isFundCallDrawerOpen, setIsFundCallDrawerOpen] = useState(false)

  // Track active tab for keyboard navigation
  const [activeTab, setActiveTab] = useState<'budgets' | 'fund-calls'>('budgets')

  // Fetch data for navigation
  const { data: budgets } = useBudgets(residenceId, fiscalYearId)
  const { data: fundCalls } = useFundCalls(residenceId, fiscalYearId)

  // Budget drawer handlers
  const handleBudgetClick = useCallback((budgetId: string) => {
    setSelectedBudgetId(budgetId)
    setIsBudgetDrawerOpen(true)
  }, [])

  const handleBudgetDrawerClose = useCallback(() => {
    setIsBudgetDrawerOpen(false)
  }, [])

  // Fund call drawer handlers
  const handleFundCallClick = useCallback((fundCallId: string) => {
    setSelectedFundCallId(fundCallId)
    setIsFundCallDrawerOpen(true)
  }, [])

  const handleFundCallDrawerClose = useCallback(() => {
    setIsFundCallDrawerOpen(false)
  }, [])

  // Budget navigation
  const budgetIndex = budgets?.findIndex((b) => b.id === selectedBudgetId) ?? -1
  const hasPreviousBudget = budgetIndex > 0
  const hasNextBudget = budgets ? budgetIndex < budgets.length - 1 : false

  const handleNavigatePreviousBudget = useCallback(() => {
    if (budgets && budgetIndex > 0) {
      const prevBudget = budgets[budgetIndex - 1]
      if (prevBudget) setSelectedBudgetId(prevBudget.id)
    }
  }, [budgets, budgetIndex])

  const handleNavigateNextBudget = useCallback(() => {
    if (budgets && budgetIndex < budgets.length - 1) {
      const nextBudget = budgets[budgetIndex + 1]
      if (nextBudget) setSelectedBudgetId(nextBudget.id)
    }
  }, [budgets, budgetIndex])

  // Fund call navigation
  const fundCallIndex = fundCalls?.findIndex((fc) => fc.id === selectedFundCallId) ?? -1
  const hasPreviousFundCall = fundCallIndex > 0
  const hasNextFundCall = fundCalls ? fundCallIndex < fundCalls.length - 1 : false

  const handleNavigatePreviousFundCall = useCallback(() => {
    if (fundCalls && fundCallIndex > 0) {
      const prevFundCall = fundCalls[fundCallIndex - 1]
      if (prevFundCall) setSelectedFundCallId(prevFundCall.id)
    }
  }, [fundCalls, fundCallIndex])

  const handleNavigateNextFundCall = useCallback(() => {
    if (fundCalls && fundCallIndex < fundCalls.length - 1) {
      const nextFundCall = fundCalls[fundCallIndex + 1]
      if (nextFundCall) setSelectedFundCallId(nextFundCall.id)
    }
  }, [fundCalls, fundCallIndex])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }

      if (activeTab === 'budgets' && isBudgetDrawerOpen) {
        if (e.key === 'ArrowUp' && hasPreviousBudget) {
          e.preventDefault()
          handleNavigatePreviousBudget()
        } else if (e.key === 'ArrowDown' && hasNextBudget) {
          e.preventDefault()
          handleNavigateNextBudget()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          handleBudgetDrawerClose()
        }
      } else if (activeTab === 'fund-calls' && isFundCallDrawerOpen) {
        if (e.key === 'ArrowUp' && hasPreviousFundCall) {
          e.preventDefault()
          handleNavigatePreviousFundCall()
        } else if (e.key === 'ArrowDown' && hasNextFundCall) {
          e.preventDefault()
          handleNavigateNextFundCall()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          handleFundCallDrawerClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    activeTab,
    isBudgetDrawerOpen,
    isFundCallDrawerOpen,
    hasPreviousBudget,
    hasNextBudget,
    hasPreviousFundCall,
    hasNextFundCall,
    handleNavigatePreviousBudget,
    handleNavigateNextBudget,
    handleNavigatePreviousFundCall,
    handleNavigateNextFundCall,
    handleBudgetDrawerClose,
    handleFundCallDrawerClose,
  ])

  return (
    <Box>
      <Tabs.Root
        defaultValue="budgets"
        variant="enclosed"
        onValueChange={(details) => setActiveTab(details.value as 'budgets' | 'fund-calls')}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Tabs.List>
            <Tabs.Trigger value="budgets">Budgets prévisionnels</Tabs.Trigger>
            <Tabs.Trigger value="fund-calls">Appels de fonds</Tabs.Trigger>
          </Tabs.List>
          <FiscalYearSelector
            residenceId={residenceId}
            value={activeFiscalYear?.id}
            onChange={setSelectedFiscalYear}
          />
        </Flex>

        <Tabs.Content value="budgets">
          <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Budgets prévisionnels</Heading>
              <Button
                colorPalette="brand"
                size="sm"
                onClick={() => setIsBudgetModalOpen(true)}
                disabled={!activeFiscalYear}
              >
                <Plus size={16} />
                Nouveau budget
              </Button>
            </Flex>
            {!activeFiscalYear ? (
              <Box textAlign="center" py={8} color="gray.500">
                Veuillez sélectionner un exercice comptable
              </Box>
            ) : (
              <>
                <ALURFundAlert
                  residenceId={residenceId}
                  fiscalYearId={fiscalYearId}
                  onCreateBudget={() => setIsBudgetModalOpen(true)}
                />
                <BudgetTable
                  residenceId={residenceId}
                  fiscalYearId={fiscalYearId}
                  selectedBudgetId={selectedBudgetId}
                  onBudgetClick={handleBudgetClick}
                />
              </>
            )}
          </Box>
        </Tabs.Content>

        <Tabs.Content value="fund-calls">
          <Box bg="white" p={5} borderRadius="lg" boxShadow="sm">
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Appels de fonds</Heading>
              <Button
                colorPalette="brand"
                size="sm"
                onClick={() => setIsFundCallModalOpen(true)}
                disabled={!activeFiscalYear}
              >
                <Plus size={16} />
                Nouvel appel
              </Button>
            </Flex>
            {!activeFiscalYear ? (
              <Box textAlign="center" py={8} color="gray.500">
                Veuillez sélectionner un exercice comptable
              </Box>
            ) : (
              <FundCallTable
                residenceId={residenceId}
                fiscalYearId={fiscalYearId}
                selectedFundCallId={selectedFundCallId}
                onFundCallClick={handleFundCallClick}
              />
            )}
          </Box>
        </Tabs.Content>
      </Tabs.Root>

      <BudgetCreateModal
        isOpen={isBudgetModalOpen}
        onClose={() => setIsBudgetModalOpen(false)}
        residenceId={residenceId}
      />

      <FundCallCreateModal
        isOpen={isFundCallModalOpen}
        onClose={() => setIsFundCallModalOpen(false)}
        residenceId={residenceId}
      />

      {/* Detail Drawers */}
      <BudgetDetailDrawer
        budgetId={selectedBudgetId}
        isOpen={isBudgetDrawerOpen}
        onClose={handleBudgetDrawerClose}
        onNavigatePrevious={handleNavigatePreviousBudget}
        onNavigateNext={handleNavigateNextBudget}
        hasPrevious={hasPreviousBudget}
        hasNext={hasNextBudget}
      />

      <FundCallDetailDrawer
        fundCallId={selectedFundCallId}
        isOpen={isFundCallDrawerOpen}
        onClose={handleFundCallDrawerClose}
        onNavigatePrevious={handleNavigatePreviousFundCall}
        onNavigateNext={handleNavigateNextFundCall}
        hasPrevious={hasPreviousFundCall}
        hasNext={hasNextFundCall}
      />
    </Box>
  )
}
